"use client";

import { createContext, type ReactNode, useContext, useState } from "react";
import { Button } from "~/shadcn/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/shadcn/ui/dialog";

const AnalyticsContext = createContext({
  showCookiePopup: false,

  acceptCookiePolicy: () => {},
});

interface CookiePolicyProviderProps {
  children: ReactNode;
  initialOpen: boolean;
}

const COOKIE_NAME = "cookie-policy-accepted";

const CookiePolicyProvider: React.FC<CookiePolicyProviderProps> = ({
  children,
  initialOpen,
}) => {
  const [showCookiePopup, setShowCookiePopup] = useState(initialOpen);

  const acceptCookiePolicy = () => {
    // Set cookie for 1 year
    const expires = new Date();
    expires.setFullYear(expires.getFullYear() + 1);
    // biome-ignore lint/suspicious/noDocumentCookie: for accepting cookie policy it's acceptable
    document.cookie = `${COOKIE_NAME}=true; path=/; expires=${expires.toUTCString()}; SameSite=Lax`;

    setShowCookiePopup(false);
  };

  return (
    <AnalyticsContext.Provider value={{ showCookiePopup, acceptCookiePolicy }}>
      {children}
      <CookiePolicyPopup open={showCookiePopup} onAccept={acceptCookiePolicy} />
    </AnalyticsContext.Provider>
  );
};

export const useAnalytics = () => {
  return useContext(AnalyticsContext);
};

const CookiePolicyPopup = ({
  onAccept,
  open,
}: {
  open: boolean;
  onAccept: () => void;
}) => {
  return (
    <Dialog open={open}>
      <DialogContent className="[&>button]:hidden">
        <DialogHeader>
          <DialogTitle>Cookie Policy</DialogTitle>

          <DialogDescription>
            Vi använder cookies för att göra din upplevelse smidig och säker. En
            del cookies används för att webbplatsen ska fungera eller
            förbättras, andra för att skräddarsy din upplevelse.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            data-cy="cookie-policy-accept"
            onClick={onAccept}
            variant="default"
          >
            Godkänn
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CookiePolicyProvider;
